import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import moment from 'moment';
import { finishUpdateStakeholderDraft } from '../../../modules/stakeholders.js';
import {
  milestonesStepValidate,
  navigateToChangeStrategy,
  navigateToStepGovernance,
  navigateToStepLinkedFiles,
  onReset,
} from './utils/index.js';
import { retrieveProject, selectProject } from '../../../modules/projects.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import StepMilestonesFrom from '../../../components/projects/ChangeStrategy/ChnageStrategyForms/StepMilestonesForm.jsx';
import {
  deleteChangeStrategyDraft,
  retrieveChangeStrategy,
  selectChangeStrategy,
  selectDraftChangeStrategy,
  updateChangeStrategy,
  updateChangeStrategyDraft,
} from '../../../modules/changeStrategy.js';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import ChangeStrategyFormHeader from '../../../components/headers/PageHeaders/Project/ChangeStrategy/ChangeStrategyFormHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save change strategy');

const mapStateToProps = (state, ownProps) => ({
  editing: !!state.changeStrategy.changeStrategy,
  changeStrategy: selectChangeStrategy(state),
  changeStrategyDraft: selectDraftChangeStrategy(state),
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
});

const mapDispatchToProps = {
  deleteChangeStrategyDraft,
  finishUpdateStakeholderDraft,
  updateChangeStrategyDraft,
  updateChangeStrategy,
  retrieveChangeStrategy,
  retrieveProject,
};

function StepMilestonesFormContainer({
  slug,
  projectId,
  editing,
  changeStrategyDraft,
  updateChangeStrategyDraft,
  updateChangeStrategy,
  deleteChangeStrategyDraft,
  project,
}) {
  const goBack = () => navigateToStepGovernance(editing, projectId, slug);
  const goNext = () => navigateToStepLinkedFiles(editing, projectId, slug);
  const goStart = () => navigateToChangeStrategy(projectId, slug);

  const onCancel = () => {
    deleteChangeStrategyDraft(projectId).then(() => {
      navigateToChangeStrategy(projectId, slug);
    });
  };

  const milestones = changeStrategyDraft?.milestones.length
    ? changeStrategyDraft.milestones.map((milestone, index) => ({
        [`id${index}`]: milestone.id,
        [`name${index}`]: milestone.name,
        [`notes${index}`]: milestone.notes,
        [`dateAt${index}`]: moment(milestone.date_at),
      }))
    : [];

  const [columns, setColumns] = useState(milestones);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const updateNotes = (value, index) => {
    setColumns((prevFields) => {
      const updatedObj = { ...prevFields[index], [`notes${index}`]: value };
      return [
        ...prevFields.slice(0, index),
        updatedObj,
        ...prevFields.slice(index + 1),
      ];
    });
  };

  const onSubmit = async (values) => {
    const { finish } = values;
    setLoading(true);
    const isValidMiles = await milestonesStepValidate(columns);

    if (Object.keys(isValidMiles).length === 0) {
      const milestones = (
        columns.length && columns[0].name0 && columns[0].dateAt0 ? columns : []
      )
        .map((milestone, index) => ({
          id: milestone[`id${index}`],
          name: milestone[`name${index}`],
          date_at: moment(milestone[`dateAt${index}`]).format('YYYY-MM-DD'),
          notes: milestone[`notes${index}`],
        }))
        .filter((milestone) => milestone.name && milestone?.date_at);
      if (finish) {
        updateChangeStrategy(
          projectId,
          { change_strategy: { milestones } },
          true,
          true
        )
          .then(() => {
            goStart();
          })
          .catch((e) => mapErrors(e));
      } else {
        updateChangeStrategyDraft(
          projectId,
          { change_strategy: { milestones } },
          true,
          true
        )
          .then(() => goNext())
          .catch((e) => mapErrors(e));
      }
    } else {
      setLoading(false);
    }
  };

  const onCreateColumn = () =>
    setColumns([
      ...columns,
      {
        id: null,
        [`name${columns.length}`]: '',
        [`dateAt${columns.length}`]: '',
        [`notes${columns.length}`]: '',
      },
    ]);

  const deleteAndAdjustIndex = (array, indexToDelete) => {
    if (indexToDelete < 0 || indexToDelete >= array.length) {
      throw new Error('Invalid index');
    }

    array.splice(indexToDelete, 1); // Remove the item at the specified index

    return array.map((item, index) => {
      const newItem = {};

      for (const key in item) {
        const match = key.match(/^(.*?)(\d+)$/);

        if (match && match[2] == index + 1) {
          const newKey = match[1] + index;
          newItem[newKey] = item[key];
        } else {
          newItem[key] = item[key];
        }
      }

      return newItem;
    });
  };

  const onDeleteColumn = (deleteIndex) => {
    const deleteMilestone = columns.find(
      (column, index) => deleteIndex === index
    );
    const name = deleteMilestone[`name${deleteIndex}`];
    if (!window.confirm('Are you sure you want to delete this milestone?'))
      return;
    const filteredMiles = deleteAndAdjustIndex(columns, deleteIndex);

    setColumns(filteredMiles);
  };

  const onColumnNameChange = (value, itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, [`name${itemIndex}`]: value } : item
      )
    );
  };

  const onColumnDateChange = (value, itemIndex) => {
    if (value) {
      setColumns(
        columns.map((item, index) =>
          itemIndex === index
            ? { ...item, [`dateAt${itemIndex}`]: moment(value) }
            : item
        )
      );
    } else {
      setColumns(
        columns.map((item, index) =>
          itemIndex === index ? { ...item, [`dateAt${itemIndex}`]: '' } : item
        )
      );
    }
  };
  const validate = () => milestonesStepValidate(columns);

  const initial = {};

  useEffect(() => {
    !changeStrategyDraft && onReset(editing, projectId, slug);
  }, []);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        editing={editing}
        subject="Change Strategy"
        typeStep="Milestones"
      >
        {project && (
          <ChangeStrategyFormHeader
            slug={slug}
            project={project}
            editing={editing}
          />
        )}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  <Form
                    component={StepMilestonesFrom}
                    validate={validate}
                    onCreateColumn={onCreateColumn}
                    onColumnNameChange={onColumnNameChange}
                    onColumnDateChange={onColumnDateChange}
                    onDeleteColumn={onDeleteColumn}
                    updateNotes={updateNotes}
                    initialValues={initial}
                    loading={loading}
                    data={columns}
                    projectId={projectId}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                    onBack={goBack}
                  />
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepMilestonesFormContainer);
